"use client";

// packages/eden-table/src/components/Table/Table.jsx
import { Children, useContext, createContext } from "react";
import styled from "styled-components";
import {
  borderColorPrimary,
  accentColorPrimary,
  fontSize14,
  backgroundColorLevelCurrent,
  grey3,
  grey60,
  setAppearanceConstantLocal
} from "@churchofjesuschrist/eden-style-constants";
import DensityContext from '../Density/Context.js';
import Col from '../Col.js';
import ColGroup from '../ColGroup.js';
import ColumnHidingContext from '../ColumnHiding/Context.js';
import SelfSelectContext from '../SelfSelect/Context.js';
import THead from '../THead.js';
import RowSelectionContext from '../RowSelection/Context.js';
import CardViewContext from '../CardView/Context.js';
import SortingContext from '../Sorting/Context.js';
import StickyHeaderContext from './StickyHeaderContext.js';
import { jsx, jsxs } from "react/jsx-runtime";
var [stripedBackgroundColorDef, stripedBackgroundColor] = setAppearanceConstantLocal("striped-background-color", {
  light: grey3,
  dark: grey60,
  prefix: "eden"
});
function getColumns(tableChildren, [columnHidingDecorator, sortingDecorator]) {
  const columns = [];
  Children.forEach(tableChildren, (colGroup) => {
    if (colGroup && colGroup.type === ColGroup) {
      if (colGroup.props.children) {
        Children.forEach(colGroup.props.children, (col) => {
          if (col?.type === Col) {
            let column = { id: col.props.id };
            column = columnHidingDecorator(column);
            column = sortingDecorator(column, colGroup, col);
            columns.push(column);
          }
        });
      } else {
        let column = { id: colGroup.props.id };
        column = columnHidingDecorator(column);
        column = sortingDecorator(column, colGroup);
        columns.push(column);
      }
    }
    const tHead = Children.toArray(tableChildren).find(
      (child) => child.type === THead
    );
    if (tHead) {
      const tHeadLastRow = Children.toArray(tHead.props.children).at(-1);
      if (tHeadLastRow) {
        const columnHeaders = Children.toArray(
          tHeadLastRow.props.children
        ).filter((header) => typeof header === "object");
        columns.forEach((column, index) => {
          column.header = columnHeaders[index].props.children;
        });
      }
    }
  });
  return columns;
}
var border = `1px solid ${borderColorPrimary}`;
var hiddenColumnBorder = `2px solid ${accentColorPrimary}`;
var Root = styled.table`
  ${stripedBackgroundColorDef}
  width: 100%;
  border-collapse: collapse;
  /* Because of the thead th box-shadows: */
  border-block-start: 0;

  /* Using custom property here so we can calculate paddings in CardView, TD, and TH. */
  --eden-table-density-divisor: ${({ $density }) => $density === "condensed" ? "2" : "1"};

  colgroup[hidden] + colgroup,
  col[hidden] + col {
    border-inline-start: ${hiddenColumnBorder};
  }

  colgroup:has(+ colgroup[hidden]),
  colgroup:has(col:last-child[hidden]) {
    border-inline-end: ${hiddenColumnBorder};
  }

  td {
    line-height: 1.25rem;
  }

  th:not(:first-child),
  td:not(:first-child) {
    border-inline-start: ${({ $dividers }) => $dividers === "full" ? border : "0"};
  }

  tbody tr {
    border-block-start: ${({ $dividers }) => $dividers === "open" ? "0" : border};
  }

  /* Once :has has more support, these can go away and we just rely on the colgroup and col styles above for the column hiding borders. See also cellDecorator in ColumnHiding. */
  [hidden] th,
  [hidden] td {
    border-inline-start: ${hiddenColumnBorder};
  }

  thead {
    ${({ $stickyHeader }) => $stickyHeader ? `
          position: sticky;
          top: 0;
          background: ${backgroundColorLevelCurrent};
          background-clip: padding-box;
          z-index: 1;
        ` : ""}
  }

  thead tr:last-of-type th,
  thead tr:last-of-type td {
    /* Simulates bottom border. Needed because border-collapse:collapse means the borders slide away with the sticky header: */
    box-shadow: inset 0px -1px 0px 0px ${borderColorPrimary};
  }

  tbody {
    font-size: ${fontSize14};
  }

  /* Because of the thead th box-shadows: */
  tbody:first-of-type tr:first-of-type {
    border-block-start: 0;
  }

  ${({ $striped }) => $striped ? `tbody tr:nth-child(odd of :not([hidden])) { background-color: ${stripedBackgroundColor()}; }` : ""}
`;
var TableContext = createContext((children) => children);
function decorateCell(originalCell, decorators = [], options) {
  return decorators.reduce((decoratedCell, decorator) => {
    return typeof decorator === "function" ? decorator(decoratedCell, options) : decoratedCell;
  }, originalCell);
}
var Table = ({
  children,
  dividers = "full",
  striped,
  role = "grid",
  density,
  stickyHeader,
  ...props
}) => {
  const densityContext = useContext(DensityContext);
  const cardView = useContext(CardViewContext);
  const columnHiding = useContext(ColumnHidingContext);
  const {
    cellDecorator: sortingCellDecorator,
    columnDecorator: sortingColumnDecorator,
    ...sorting
  } = useContext(SortingContext);
  const columnsRef = useContext(SelfSelectContext);
  const rowSelection = useContext(RowSelectionContext);
  const stickyHeaderContext = useContext(StickyHeaderContext);
  const columns = getColumns(children, [
    columnHiding.columnDecorator,
    sortingColumnDecorator
  ]);
  if (columnsRef) {
    columnsRef.current = columns;
  }
  function renderRowChildren(rowChildren) {
    return Children.toArray(rowChildren).map((cell, index) => {
      return decorateCell(
        cell,
        [
          cardView,
          sortingCellDecorator,
          // Needs to occur after sorting so we don't end up with buttons in buttons in the column headers
          columnHiding.cellDecorator
        ],
        {
          rowChildren,
          index,
          columns,
          sorting
        }
      );
    });
  }
  return /* @__PURE__ */ jsxs(
    Root,
    {
      ...props,
      $dividers: dividers,
      $striped: striped,
      $density: densityContext || density,
      $stickyHeader: stickyHeaderContext || stickyHeader,
      role,
      children: [
        rowSelection ? /* @__PURE__ */ jsx(rowSelection.RowSelectionColGroup, {}) : null,
        /* @__PURE__ */ jsx(TableContext.Provider, { value: renderRowChildren, children: /* @__PURE__ */ jsx(
          StickyHeaderContext.Provider,
          {
            value: stickyHeaderContext || stickyHeader,
            children
          }
        ) })
      ]
    }
  );
};
Table.displayName = "Table";
var Table_default = Table;
export {
  TableContext,
  Table_default as default
};
